import { Box, Typography } from '@mui/material';
import { ErrorAlert } from '@/components/forms';
import { SkeletonBlock } from '@/components/general/skeletonBlock/SkeletonBlock';
import Table from '@/components/general/Table/Table';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { usePhrase } from '@/hooks';
import { useTransactionHistoryQuery } from '@/hooks/queries/wallet/walletQueries';
import { localeDict } from '@/consts/languageAccordance';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { TransactionDate } from '../../../../general/TransactionDate/TransactionDate';
import { config } from '@/config';
import { useLocale } from '@/hooks/general/useLocale/useLocale';

interface TransactionHistoryProps {
    transactionId: number;
    token?: string;
}

export const TransactionHistory = (props: TransactionHistoryProps) => {
    const { t } = usePhrase();
    const { locale } = useLocale();
    const languageId =
        localeDict[locale].language.id || localeDict[config.defaultLocale].language.id;

    const { transactionHistory, error } = useTransactionHistoryQuery(
        props.transactionId,
        languageId,
        props.token,
    );
    const errorMessage = getFetchErrors(error);
    return (
        <Box sx={{ width: [250, 500] }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t(WALLET_PHRASES.REFERENCE_NUMBER)}: {props.transactionId}
            </Typography>
            {!transactionHistory && !error && <SkeletonBlock amount={2} height={50} />}
            {error && <ErrorAlert id="error_message" errorMessage={errorMessage} />}
            {transactionHistory && (
                <Table
                    tableData={{
                        details: { 'aria-label': 'transactions-history-table' },
                        headers: [
                            { label: t(WALLET_PHRASES.DATE), key: 'date' },
                            { label: t(WALLET_PHRASES.ACTION), key: 'action' },
                            { label: t(WALLET_PHRASES.NOTES), key: 'notes' },
                        ],
                        rows: transactionHistory?.map((data, idx) => ({
                            key: data.createDt ?? idx,
                            values: [
                                {
                                    label: data.createDt ? (
                                        <TransactionDate date={data.createDt} />
                                    ) : (
                                        ''
                                    ),
                                    key: 'date',
                                },
                                {
                                    label: data.actionPhraseId ? t(data.actionPhraseId) : '',
                                    key: 'action',
                                },
                                {
                                    label: data.memberNotes ? data.memberNotes : '',
                                    key: 'notes',
                                },
                            ],
                        })),
                    }}
                    paginationVisible={false}
                />
            )}
        </Box>
    );
};
